import React, {useEffect, useState} from 'react'
import PropTypes from 'prop-types'
import Header from '../components/Header';
import {useQueryClient} from "react-query";
import {useAuth} from "../authentication/AuthContext";
import {useLocation, useNavigate} from "react-router-dom";
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import {Flag, Play} from 'lucide-react';
import EmptyTaskResults from "./EmptyTaskResults";
import {useCreateTask, useDeleteTask, useTask} from "./hooks/useTasks";
import AddTaskModal from "./components/AddTaskModal";
import {useProject, useProjects} from "../project/hooks/useProjects";
import Sidebar from '../components/Sidebar';
import {authFetch} from "../authentication/authFetch";

const API_URL = process.env.REACT_APP_API_URL;

function formatDateKey(deadlineDate) {
    if (!deadlineDate) return null;
    const d = new Date(deadlineDate); // Unix ms timestamp
    const yyyy = d.getFullYear();
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const dd = String(d.getDate()).padStart(2, '0');
    return `${yyyy}-${mm}-${dd}`;
}

function getDayLabel(dateKey) {
    if (!dateKey) return 'No Deadline';
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const target = new Date(dateKey + 'T00:00:00');
    const diffMs = target - today;
    const diffDays = Math.round(diffMs / (1000 * 60 * 60 * 24));

    if (diffDays < 0) {
        return `Past due — ${target.toLocaleDateString('en-En', {day: 'numeric', month: 'long', year: 'numeric'})}`;
    }
    if (diffDays === 0) return 'Today';
    if (diffDays === 1) return 'Tomorrow';
    return target.toLocaleDateString('en-EN', {weekday: 'long', day: 'numeric', month: 'long', year: 'numeric'});
}

function groupTasksByDeadline(tasks) {
    const groups = {};
    for (const task of tasks) {
        const key = formatDateKey(task.deadlineDate) ?? '__no_deadline__';
        if (!groups[key]) groups[key] = [];
        groups[key].push(task);
    }

    // Sort keys: past dates first, then today, tomorrow, future, then no deadline
    const sortedKeys = Object.keys(groups).sort((a, b) => {
        if (a === '__no_deadline__') return 1;
        if (b === '__no_deadline__') return -1;
        return new Date(a) - new Date(b);
    });

    return sortedKeys.map(key => ({
        key,
        label: getDayLabel(key === '__no_deadline__' ? null : key),
        isOverdue: key !== '__no_deadline__' && new Date(key + 'T00:00:00') < new Date(new Date().setHours(0,0,0,0)),
        tasks: groups[key],
    }));
}

function getPriorityFlagClass(priority) {
    if (priority === 'HIGH') return 'text-red-400';
    if (priority === 'MEDIUM') return 'text-yellow-400';
    if (priority === 'LOW') return 'text-blue-400';
    return 'text-[var(--text-muted)]';
}

/* eslint-disable react/prop-types */
function TaskRow(props) {
    const {todo, authToken, navigate, deleteTask, handleCheckbox} = props;
    const {data: project} = useProject(authToken, todo.projectId);
    const priority = todo.priority ?? todo.taskPriority;
    const handleOpenTask = () => navigate(`/task/${todo.id}`);

    return (
        <div className='group relative flex items-center justify-between rounded-xl border border-[var(--border-color)] bg-[var(--surface-deep)] px-4 py-2 text-[var(--text-primary)] transition hover:bg-[var(--surface-hover)]'>
            <div className='flex min-w-0 flex-1 items-center gap-2'>
                <input
                    className='w-5 h-5 accent-[var(--brand-coral)] rounded focus:ring-2 focus:ring-[var(--brand-coral)] transition-all flex-shrink-0'
                    checked={todo.taskStatus === 'COMPLETED'}
                    type="checkbox"
                    onChange={(event) => handleCheckbox(todo.id, event.target.checked)}
                />
                <button
                    type="button"
                    onClick={handleOpenTask}
                    className='flex min-w-0 flex-1 items-center gap-2 text-left'
                >
                    <span className={todo.taskStatus === 'COMPLETED' ? 'line-through text-[var(--text-muted)]' : ''}>
                        {todo.text}
                    </span>
                    {priority && (
                        <Flag size={14} className={getPriorityFlagClass(priority)} />
                    )}
                    {project?.name && (
                        <span className="inline-flex items-center rounded-full border border-[var(--brand-coral)]/40 bg-[var(--brand-coral)]/10 px-2 py-0.5 text-xs text-[var(--brand-coral)]">
                            {project.name}
                        </span>
                    )}
                </button>
            </div>
            <div className='ml-2 hidden gap-2 group-hover:flex'>
                <button
                    className='px-2 py-1 hover:text-[var(--brand-coral)]'
                    title="Start work session"
                    onClick={(e) => { e.stopPropagation(); navigate('/session', { state: { taskId: todo.id, projectId: todo.projectId } }); }}
                >
                    <Play size={16}/>
                </button>
                <button className='px-2 py-1 hover:text-red-400' onClick={(e) => { e.stopPropagation(); deleteTask(todo.id); }}>
                    <DeleteIcon fontSize="small"/>
                </button>
            </div>
        </div>
    );
}

TaskRow.propTypes = {
    todo: PropTypes.shape({
        id: PropTypes.string.isRequired,
        text: PropTypes.string,
        taskStatus: PropTypes.string,
        projectId: PropTypes.string,
        priority: PropTypes.string,
        taskPriority: PropTypes.string,
    }).isRequired,
    authToken: PropTypes.string,
    navigate: PropTypes.func.isRequired,
    deleteTask: PropTypes.func.isRequired,
    handleCheckbox: PropTypes.func.isRequired,
};
/* eslint-enable react/prop-types */

function Task() {
    const queryClient = useQueryClient()
    const {authToken} = useAuth();
    const navigate = useNavigate();
    const location = useLocation();
    const {data: task} = useTask(authToken)
    const {data: projects} = useProjects(authToken)
    const {mutate: deleteTask} = useDeleteTask(authToken)
    const {mutate: createTask} = useCreateTask(authToken)

    const [showModal, setShowModal] = useState(false);
    const [showEditModal, setShowEditModal] = useState(false);
    const [showArchived, setShowArchived] = useState(true);
    const [pageSize, setPageSize] = useState(25);
    const [currentPage, setCurrentPage] = useState(1);

    useEffect(() => {
        if (!location.state?.openAddTaskModal) return;

        setShowModal(true);
        navigate(location.pathname, {replace: true, state: null});
    }, [location.pathname, location.state, navigate]);

    const handleSubmit = (inputValue) => {
        createTask(inputValue)
        setShowModal(false)
    }

    const handleCheckbox = async (id, checked) => {
        const taskStatus = checked ? 'COMPLETED' : 'PENDING'
        await authFetch(`${API_URL}/api/task/${id}?taskStatus=${taskStatus}`, {
            method: 'PUT',
            headers: {
                'Content-Type': 'application/json',
                authorization: `Bearer ${authToken}`,
            }
        })
        await queryClient.invalidateQueries(['todo'])
    }

    const activeTasks = task ? task.filter(t => t.taskStatus !== 'COMPLETED') : [];
    const completedTasks = task ? task.filter(t => t.taskStatus === 'COMPLETED') : [];
    const totalPages = Math.max(1, Math.ceil(activeTasks.length / pageSize));
    const safeCurrentPage = Math.min(currentPage, totalPages);
    const pageStart = (safeCurrentPage - 1) * pageSize;
    const paginatedActiveTasks = activeTasks.slice(pageStart, pageStart + pageSize);
    const grouped = paginatedActiveTasks.length > 0 ? groupTasksByDeadline(paginatedActiveTasks) : [];

    useEffect(() => {
        if (currentPage > totalPages) {
            setCurrentPage(totalPages);
        }
    }, [currentPage, totalPages]);

    return (
        <div className="flex min-h-screen flex-col bg-[var(--bg)]">
            <Header/>
            <div className="flex flex-1 gap-3 bg-[var(--workspace-bg)] px-3 pb-4 pt-[76px] md:gap-4 md:px-4">
                <Sidebar />
                <div className="flex min-w-0 flex-1 flex-col rounded-[10px] bg-[var(--surface)]">
                    <div className="mx-auto flex w-full max-w-3xl flex-1 flex-col px-4 py-8 md:px-6 md:py-10">
                        <div className='mb-6 flex justify-end'>
                            <button
                                className="inline-flex items-center gap-2 rounded-md border border-[var(--button-primary-bg)] bg-[var(--button-primary-bg)] px-4 py-2 text-sm font-semibold text-[var(--button-primary-text)] shadow-sm transition hover:brightness-105 active:brightness-95"
                                onClick={() => setShowModal(true)}
                            >
                                <AddIcon/>
                                Add Task
                            </button>
                        </div>

                        {grouped.length > 0 ? (
                            grouped.map(group => (
                                <div key={group.key} className="mb-8">
                                    {/* Day header */}
                                    <div className="mb-3 flex items-center gap-3">
                                        <span className={`text-sm font-semibold uppercase tracking-wide ${group.isOverdue ? 'text-red-400' : 'text-[var(--brand-coral)]'}`}>
                                            {group.label}
                                        </span>
                                        <div className="h-px flex-1 bg-[var(--surface-deep)]"/>
                                        <span className="text-xs text-[var(--text-muted)]">{group.tasks.length} task</span>
                                    </div>

                                    {/* Tasks in this group */}
                                    <div className="flex flex-col gap-2">
                                        {group.tasks.map((todo) => (
                                            <TaskRow
                                                key={todo.id}
                                                todo={todo}
                                                authToken={authToken}
                                                navigate={navigate}
                                                deleteTask={deleteTask}
                                                handleCheckbox={handleCheckbox}
                                            />
                                        ))}
                                    </div>
                                </div>
                            ))
                        ) : <EmptyTaskResults/>}

                        {activeTasks.length > 0 && (
                            <div className="order-last mt-auto flex flex-col gap-3 rounded-xl border border-[var(--border-color)] bg-[var(--surface)] px-4 py-3 sm:flex-row sm:items-center sm:justify-between">
                                <div className="flex items-center gap-2 text-xs text-[var(--text-muted)]">
                                    <span>Rows per page</span>
                                    <select
                                        value={pageSize}
                                        onChange={(event) => {
                                            setPageSize(Number(event.target.value));
                                            setCurrentPage(1);
                                        }}
                                        className="rounded-md border border-[var(--border-color)] bg-[var(--surface-deep)] px-2 py-1 text-sm text-[var(--text-primary)] outline-none focus:border-[var(--brand-coral)]"
                                        aria-label="Tasks per page"
                                    >
                                        {[25, 50, 100].map(size => (
                                            <option key={size} value={size}>{size}</option>
                                        ))}
                                    </select>
                                    <span>
                                        {pageStart + 1}–{Math.min(pageStart + pageSize, activeTasks.length)} of {activeTasks.length}
                                    </span>
                                </div>

                                <div className="flex items-center gap-2">
                                    <button
                                        type="button"
                                        onClick={() => setCurrentPage(page => Math.max(1, page - 1))}
                                        disabled={safeCurrentPage === 1}
                                        className="rounded-md border border-[var(--border-color)] px-3 py-1.5 text-xs font-medium text-[var(--text-secondary)] transition hover:bg-[var(--surface-deep)] disabled:cursor-not-allowed disabled:opacity-40"
                                    >
                                        Previous
                                    </button>
                                    <span className="min-w-20 text-center text-xs text-[var(--text-muted)]">
                                        Page {safeCurrentPage} of {totalPages}
                                    </span>
                                    <button
                                        type="button"
                                        onClick={() => setCurrentPage(page => Math.min(totalPages, page + 1))}
                                        disabled={safeCurrentPage === totalPages}
                                        className="rounded-md border border-[var(--border-color)] px-3 py-1.5 text-xs font-medium text-[var(--text-secondary)] transition hover:bg-[var(--surface-deep)] disabled:cursor-not-allowed disabled:opacity-40"
                                    >
                                        Next
                                    </button>
                                </div>
                            </div>
                        )}

                        {/* Archived (Completed) Tasks */}
                        {completedTasks.length > 0 && (
                            <div className="mt-10">
                                <button
                                    onClick={() => setShowArchived(prev => !prev)}
                                    className="mb-3 flex w-full items-center gap-3 text-left"
                                >
                                    <span className="text-sm font-semibold uppercase tracking-wide text-[var(--text-muted)]">
                                        Completed / Archived
                                    </span>
                                    <div className="h-px flex-1 bg-[var(--surface-deep)]/50"/>
                                    <span className="text-xs text-[var(--text-muted)]">{completedTasks.length} task</span>
                                    <span className={`text-[var(--text-muted)] transition-transform duration-200 ${showArchived ? 'rotate-180' : ''}`}>▾</span>
                                </button>
                                {showArchived && (
                                    <div className="flex flex-col gap-2 opacity-50">
                                        {completedTasks.map((todo) => (
                                            <TaskRow
                                                key={todo.id}
                                                todo={todo}
                                                authToken={authToken}
                                                navigate={navigate}
                                                deleteTask={deleteTask}
                                                handleCheckbox={handleCheckbox}
                                            />
                                        ))}
                                    </div>
                                )}
                            </div>
                        )}

                        {showModal && <AddTaskModal projects={projects ?? []} onSubmit={handleSubmit} onClose={() => setShowModal(false)}/>}
                        {showEditModal && <AddTaskModal onClose={() => setShowEditModal(false)} onSubmit={() => setShowEditModal(false)}/>}
                    </div>
                </div>
            </div>
        </div>
    )
}

export default Task
